import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

interface ChartSeries {
  name: string;
  data: (number | null)[];
}

interface Props {
  data: { categories: string[]; series: ChartSeries[] };
  title?: string;
  neighborhoodName?: string;
}

const FONT = "'Montserrat Light', 'Montserrat', sans-serif";
const FONT_SIZE = '11pt';
const FONT_STYLE = { fontFamily: FONT, fontSize: FONT_SIZE, fontWeight: 'normal' as const };

// Luxury = blue, Traditional = gold
const SERIES_COLORS = ['#738FAF', '#CBA968'];
const MAX_VISIBLE_LABELS = 15;

export const LuxVsTraditionalChart = React.forwardRef<HighchartsReact.RefObject, Props>(
  ({ data, title = 'Sales Prices - Luxury vs Traditional', neighborhoodName = '' }, ref) => {
    const subtitle = `${neighborhoodName} | Median Selling Price Comparison`;

    const lastIndex = data.categories.length - 1;
    const labelStep = Math.max(1, Math.ceil(data.categories.length / MAX_VISIBLE_LABELS));
    const currencyFormatter: Highcharts.DataLabelsFormatterCallbackFunction = function () {
      if (this.y == null) return '';
      if (labelStep > 1 && this.index % labelStep !== 0 && this.index !== lastIndex) return '';
      return '$' + Highcharts.numberFormat(this.y, 0);
    };

    const options: Highcharts.Options = {
      chart: {
        type: 'line',
        height: 560,
        marginTop: 120,
        style: { fontFamily: FONT, fontSize: FONT_SIZE }
      },
      title: {
        text: title,
        align: 'left',
        style: { fontFamily: "'Montserrat', sans-serif", fontSize: '30pt', fontWeight: '500', color: '#000000' }
      },
      subtitle: {
        text: subtitle,
        align: 'left',
        style: { fontFamily: "'Montserrat', sans-serif", fontSize: '14pt', fontWeight: '500', color: '#66635F' }
      },
      xAxis: {
        categories: data.categories,
        title: { text: 'Year', style: FONT_STYLE },
        labels: { style: FONT_STYLE }
      },
      yAxis: {
        title: { text: undefined },
        labels: {
          style: FONT_STYLE,
          formatter: function () {
            return '$' + Highcharts.numberFormat(this.value as number, 0);
          }
        },
        min: 0
      },
      legend: {
        itemStyle: FONT_STYLE
      },
      tooltip: {
        style: FONT_STYLE,
        formatter: function () {
          return this.points!.map(p =>
            `<span style="color:${p.color}">${p.series.name}</span>: <b>$${Highcharts.numberFormat(p.y!, 0)}</b>`
          ).join('<br/>');
        },
        shared: true
      },
      plotOptions: {
        line: {
          marker: { enabled: true, radius: 4 },
          connectNulls: false,
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            formatter: currencyFormatter,
            style: {
              fontFamily: FONT,
              fontSize: FONT_SIZE,
              fontWeight: 'normal',
              textOutline: 'none',
              color: '#000000'
            }
          }
        }
      },
      colors: SERIES_COLORS,
      series: data.series.map(s => ({ type: 'line' as const, name: s.name, data: s.data })),
      credits: { enabled: false }
    };

    return <HighchartsReact ref={ref} highcharts={Highcharts} options={options} />;
  }
);
